import {
  BodyMedium,
  CalendarIcon,
  CompanyIcon,
  evaluateKpi,
  GdprIcon,
  KPI_METRIC_TYPE,
  LanguageSelector,
  theme,
} from '@arnold/common';
import { useRef, type ComponentProps, type RefObject } from 'react';
import styled from '@emotion/styled';
import { useTranslation } from 'react-i18next';
import { Link, useLocation } from 'react-router-dom';
import type { CustomMetricSummary } from '@arnold/core';
import { MetricValue } from '../definition/MetricValue';
import type { MetricFormat } from '../definition/types';
import UserMenu from '../../../components/UserMenu';
import { IsAccessTokenValidQuery, QuestionType } from '../../../generated/hooks';
import { getPrintableReportDate } from '../../../lib/reportHelper';
import {
  CollapsibleInfo,
  CollapsibleInfoInner,
  Header,
  HeaderRow,
  HeaderSlot,
  HeaderRowContent,
  InfoContainer,
  InfoItem,
  InfoRow,
  Separator,
  Title,
  UserMenuContainer,
} from '../../newReportPageStyles';
import { useFloatingHeaderHeight } from '../hooks/useFloatingHeaderHeight';
import { ExportReportButton } from './ExportReportButton';
import { ReportFilters } from './ReportFilters';
import { RootTeamsSummary } from './RootTeamsSummary';
import { ScaleScore } from './ScaleCard';
import { QuestionSummary } from '../types';

interface ReportHeaderProps {
  detail?: { overviewUrl: string; subtitle?: string; metric?: CustomMetricSummary; metricFormat?: MetricFormat };
  title?: string;
  description?: string;
  isCompact: boolean;
  /** The scrollable element the header floats above; receives the header height CSS variables. */
  scrollerRef: RefObject<HTMLElement>;
  surveyStart: string;
  surveyEnd: string | null | undefined;
  anonymityMode: string;
  organizationName: string;
  rootTeamNames: string[];
  selectedLanguage?: string;
  allowedLanguages?: string[];
  onChangeLanguage: (languageCode: string) => void;
  user?: IsAccessTokenValidQuery['isAccessTokenValid']['user'];
  filtersProps: Omit<ComponentProps<typeof ReportFilters>, 'compact'>;
  questionSummary?: QuestionSummary;
}

const Breadcrumb = styled.nav`
  font-size: 13px;
  margin-bottom: 12px;
  color: ${theme.colors.text.secondary};
  a {
    color: ${theme.colors.actionPrimary.default};
  }
`;
const DetailSubtitle = styled.p`
  font-size: 13px;
  color: ${theme.colors.text.secondary};
  margin: 0;
`;

export const ReportHeader = ({
  detail,
  title,
  description,
  isCompact,
  scrollerRef,
  surveyStart,
  surveyEnd,
  anonymityMode,
  organizationName,
  rootTeamNames,
  selectedLanguage,
  allowedLanguages,
  onChangeLanguage,
  user,
  filtersProps,
  questionSummary,
}: ReportHeaderProps) => {
  const { t: tReport } = useTranslation('Report');
  const { t } = useTranslation('newReport');
  const { t: tKpiCard } = useTranslation('kpiCard');
  const { search } = useLocation();
  const headerRef = useRef<HTMLElement>(null);
  useFloatingHeaderHeight(headerRef, scrollerRef, isCompact);

  const metricSummary = detail?.metric;
  const isScaleQuestion = questionSummary?.type === QuestionType.Scale;
  const averageValue = isScaleQuestion ? questionSummary.average.value : metricSummary?.value;
  const averageMax = isScaleQuestion ? questionSummary.average.max : metricSummary?.max;
  const showScaleScore = !!detail && isScaleQuestion && !!questionSummary.average.count && averageValue !== undefined;
  const scaleEvaluation =
    averageValue !== undefined ? evaluateKpi(averageValue, KPI_METRIC_TYPE.SCALE, tKpiCard) : undefined;
  return (
    <HeaderSlot>
      <Header ref={headerRef} compact={isCompact}>
        {detail && (
          <Breadcrumb>
            <Link to={{ pathname: detail.overviewUrl, search }}>{t('menu.overview')}</Link>
            <span> / {title}</span>
          </Breadcrumb>
        )}
        <HeaderRow compact={isCompact}>
          <HeaderRowContent>
            <Title compact={isCompact}>{title}</Title>
            {!detail && <ExportReportButton compact={isCompact} />}
            {showScaleScore && questionSummary && averageValue !== undefined && (
              <div style={{ minWidth: 100 }}>
                <ScaleScore
                  value={averageValue}
                  max={averageMax ?? 1}
                  badgeText={scaleEvaluation?.badgeText}
                  state={scaleEvaluation?.state}
                />
              </div>
            )}
          </HeaderRowContent>
          <UserMenuContainer>
            {selectedLanguage && (
              <LanguageSelector
                lang={selectedLanguage}
                allowedLanguages={Object.freeze(allowedLanguages)}
                onChange={onChangeLanguage}
              />
            )}

            <UserMenu user={user} />
          </UserMenuContainer>
        </HeaderRow>
        {isCompact && <Separator />}
        <CollapsibleInfo compact={isCompact}>
          <CollapsibleInfoInner>
            <InfoContainer>
              {!detail && (
                <InfoRow>
                  <InfoItem>
                    <CalendarIcon />
                    <BodyMedium>{getPrintableReportDate(surveyStart, surveyEnd, tReport)}</BodyMedium>
                  </InfoItem>
                  <InfoItem>
                    <GdprIcon />
                    <BodyMedium>{tReport(`anonymityMode.${anonymityMode}`)}</BodyMedium>
                  </InfoItem>
                  <InfoItem>
                    <CompanyIcon />
                    <BodyMedium>{organizationName}</BodyMedium>
                  </InfoItem>
                  <RootTeamsSummary names={rootTeamNames} />
                </InfoRow>
              )}
              {description && <BodyMedium>{description}</BodyMedium>}
              {detail?.subtitle && <DetailSubtitle>{detail.subtitle}</DetailSubtitle>}
              {detail?.metric && questionSummary?.type !== QuestionType.Scale && (
                <div style={{ maxWidth: 500, marginTop: 16 }}>
                  <MetricValue summary={detail.metric} format={detail.metricFormat ?? 'number'} />
                </div>
              )}
            </InfoContainer>
            <Separator />
          </CollapsibleInfoInner>
        </CollapsibleInfo>
        <ReportFilters {...filtersProps} compact={isCompact} />
      </Header>
    </HeaderSlot>
  );
};
